// Lightweight wrapper around window.gtag for custom tool-flow events.
// Safe to call from any client component — no-ops on the server, no-ops if
// gtag is not yet defined, and no-ops if the user has not granted
// analytics_storage consent (GTM enforces that at the tag level).

export type ToolEventName =
    | 'tool_upload_started'
    | 'tool_conversion_started'
    | 'tool_conversion_completed'
    | 'tool_download_clicked'

export interface ToolEventParams {
    /** The tool identifier (e.g. 'image-compressor', 'pdf-compressor', 'converter', 'remove-background', 'video-compressor', or a conversion-pair slug like 'jpg-to-png') */
    tool: string
    /** How many files were involved in this event */
    file_count?: number
    /** Category of files being processed */
    file_kind?: 'image' | 'video' | 'audio' | 'pdf' | 'unknown'
    /** For converter flows, the chosen output format (e.g. 'jpg', 'mp4') */
    target_format?: string
    /** For conversion-pair pages, the slug of the pair (e.g. 'jpg-to-png') */
    pair_slug?: string
    /** For download events, whether the user used the bulk Download All button */
    is_bulk?: boolean
    /** For completed events, how many files reached a successful state */
    success_count?: number
    /** For completed events, how many files failed */
    failure_count?: number
}

export function trackToolEvent(name: ToolEventName, params: ToolEventParams): void {
    if (typeof window === 'undefined') return
    // IMPORTANT — push the OBJECT shape `{event: name, ...}` to dataLayer, NOT
    // gtag('event', name, params). gtag pushes the array shape
    // `['event', name, {...}]`, which GTM's Custom Event trigger does NOT match,
    // so the GA4 tag never fires and the event never reaches GA4. The object
    // shape below is what GTM Custom Event triggers actually listen for — same
    // approach used by HumanityTracker (the only custom event that worked).
    // Consent is still enforced by GTM at the tag level.
    try {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({ event: name, ...params })
    } catch {
        // Swallow — analytics must never break the app
    }
}
